import React, { useState } from 'react';
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale, LinearScale, BarElement, Tooltip, Legend,
} from 'chart.js';
import ChartCard from '../components/chart/ChartCard';
import { useDashboardData } from '../data/DashboardDataProvider';
import { colors } from '../utils/colors';

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip, Legend);

const segments = ['paid_ever', 'multiple_songs', 'finished', 'started', 'remaining_users'];
const backgroundColors = [colors.primary, colors.teal, colors.yellow, colors.darkorange, colors.grey5];

const programActionTypes = ['program-start', 'program-quit', 'play-song', 'play-snippet'];
const onboardingActionTypes = ['welcome-screen-play', 'welcome-screen-write', 'welcome-screen-close', 'start-info-link', 'link-in-song',
  'start-wizard-browse', 'start-wizard-example', 'start-wizard-import', 'start-wizard-play', 'start-wizard-tour', 'start-wizard-write',
  'start-info-ok', 'close-button'];
const startSongActionTypes = ['audio-import', 'audio-recording', 'midi-import-begin', 'midi-import', 'midi-recording-begin', 'midi-recording',
  'musicxml-import-begin', 'musicxml-import', 'new-empty-song', 'open-song-from-disk', 'open-song-from-cloud', 'recording-start', 'youtube-import'];
const processActionTypes = ['recording-stop', 'midi-recording-end', 'midi-import-end', 'musicxml-import-end', 'snippet-to-song-begin', 'snippet-to-song-end',
  'analysis-aborted', 'cloud-analysis-begin', 'cloud-analysis-upload-finish', 'cloud-analysis-end', 'snippet-ready', 'chord-analysis-ready', 'song-ready', 'song-display', 'analysis-result-missing'];
const changeModeActionTypes = ['set-mode-to-normal', 'set-mode-to-manual', 'set-mode-to-voice', 'set-mode-to-mixer'];
const finishSongActionTypes = ['save-song', 'save-new-song', 'print-song', 'share-song', 'export-song-as-midi', 'export-song-as-audio', 'export-song-as-musicxml'];
const allKnown = [...programActionTypes, ...onboardingActionTypes, ...startSongActionTypes, ...processActionTypes, ...changeModeActionTypes, ...finishSongActionTypes];

const SegmentEventsPage = ({ dataKey, headerPrefix, description }) => {
  const { segmentEventData, errors } = useDashboardData();
  const eventsData = segmentEventData[dataKey];
  const [showUsers, setShowUsers] = useState(true);
  const [sortByTotal, setSortByTotal] = useState(true);
  const [showPercentage, setShowPercentage] = useState(true);
  const [datasetVisibility, setDatasetVisibility] = useState({});
  const [eventFilters, setEventFilters] = useState({
    onboarding: true, startSong: true, process: false, changeMode: true, finishSong: true, program: true, remaining: true,
  });

  const isInIncludedCategory = (eventName) => {
    if (onboardingActionTypes.includes(eventName) && !eventFilters.onboarding) return false;
    if (startSongActionTypes.includes(eventName) && !eventFilters.startSong) return false;
    if (processActionTypes.includes(eventName) && !eventFilters.process) return false;
    if (changeModeActionTypes.includes(eventName) && !eventFilters.changeMode) return false;
    if (finishSongActionTypes.includes(eventName) && !eventFilters.finishSong) return false;
    if (programActionTypes.includes(eventName) && !eventFilters.program) return false;
    if (!allKnown.includes(eventName) && !eventFilters.remaining) return false;
    return true;
  };

  const filterAndSort = (data) => {
    let filtered = data.filter(item => {
      if (item.event_name === 'TOTAL_USERS' || item.event_name === 'grouped-events') return false;
      if (!isInIncludedCategory(item.event_name)) return false;
      return segments.some(seg => parseInt(item[seg], 10) > 50 || parseInt(item[`${seg}_users`], 10) > 50);
    });
    if (sortByTotal) {
      filtered = [...filtered].sort((a, b) => {
        const totalA = segments.reduce((sum, c) => sum + (parseInt(showUsers ? a[`${c}_users`] : a[c], 10) || 0), 0);
        const totalB = segments.reduce((sum, c) => sum + (parseInt(showUsers ? b[`${c}_users`] : b[c], 10) || 0), 0);
        return totalB - totalA;
      });
    }
    return filtered;
  };

  const filteredData = filterAndSort(eventsData.data);
  const totalRow = eventsData.data.find(item => item.event_name === 'TOTAL_USERS') || {};
  const labels = filteredData.map(e => e.event_name);

  const datasets = segments.map((segment, i) => {
    const total = parseInt(showUsers ? totalRow[`${segment}_users`] : totalRow[segment], 10) || 1;
    return {
      label: segment,
      backgroundColor: backgroundColors[i % backgroundColors.length],
      hidden: datasetVisibility[segment] === false,
      data: filteredData.map(event => {
        const value = parseInt(showUsers ? event[`${segment}_users`] : event[segment], 10) || 0;
        return showPercentage ? Math.round((value / total) * 10000) / 100 : value;
      }),
    };
  });

  const filterCategories = { program: 'Program Events', onboarding: 'Onboarding', startSong: 'Start Song', process: 'Processing', changeMode: 'Change Mode', finishSong: 'Finish Song', remaining: 'Editing (remaining)' };

  return (
    <section>
      <div className="chart-grid">
        <div className="chart-col-full">
          <div className="form-group-row">
            <label className="form-check"><input type="checkbox" checked={showUsers} onChange={() => setShowUsers(p => !p)} /> Show Users Instead of Event Counts</label>
            <label className="form-check"><input type="checkbox" checked={sortByTotal} onChange={() => setSortByTotal(p => !p)} /> Sort by Total</label>
            <label className="form-check"><input type="checkbox" checked={showPercentage} onChange={() => setShowPercentage(p => !p)} /> Show as Percentage of Segment Size</label>
          </div>
          <hr className="divider" />
          <div className="form-group-row">
            {Object.entries(filterCategories).map(([key, label]) => (
              <label key={key} className="form-check"><input type="checkbox" checked={eventFilters[key]} onChange={() => setEventFilters(p => ({ ...p, [key]: !p[key] }))} /> {label}</label>
            ))}
          </div>
        </div>

        <div className="chart-col-full">
          <ChartCard
            header={`${headerPrefix} - ${showUsers ? 'User Counts' : 'Event Counts'} by Segment${showPercentage ? ' (%)' : ''}`}
            loading={eventsData.loading} error={errors.segmentEvents} description={description} hideAvg>
            <table style={{ marginBottom: 8 }}>
              <thead><tr>{segments.map(s => <th key={s}>{s}</th>)}</tr></thead>
              <tbody><tr>{segments.map(s => <td key={s}>{showUsers ? totalRow[`${s}_users`] : totalRow[s]}</td>)}</tr></tbody>
            </table>
            <div style={{ height: `${labels.length * 40 + 180}px` }}>
              <Bar
                data={{ labels, datasets }}
                options={{
                  indexAxis: 'y',
                  responsive: true,
                  maintainAspectRatio: false,
                  animation: false,
                  plugins: {
                    legend: {
                      position: 'top',
                      onClick: (_, legendItem) => {
                        setDatasetVisibility(prev => ({ ...prev, [legendItem.text]: !(prev[legendItem.text] !== false) }));
                      },
                    },
                  },
                  scales: {
                    x: { stacked: false, beginAtZero: true, title: { display: true, text: showPercentage ? 'Percentage (%)' : (showUsers ? 'Number of Users' : 'Number of Events') } },
                    y: { stacked: false, title: { display: true, text: 'Event Types' } },
                  },
                }}
              />
            </div>
          </ChartCard>
        </div>

        <div className="chart-col-full">
          <div className="card">
            <div className="card-content" style={{ fontSize: 12, color: 'rgb(123, 128, 154)' }}>
              <p style={{ marginBottom: 8 }}>Note: graphs select segments based on user_id. Initial program starts (not logged in) are not included. In percentage of users, program-starts are 100%.</p>
              <p style={{ marginBottom: 8 }}>Segments are users with events in the timeframe. Users in one segment will not be included in the next.</p>
              <p><b>paid_ever</b>: Users that have at any point had a paying subscription.</p>
              <p><b>multiple_songs</b>: Users that has saved 5+ songs from Studio or Songwriter.</p>
              <p><b>finished</b>: Users that have triggered a "finishing event" (save, export, share, print).</p>
              <p><b>started</b>: Users with a "starting event" but never a "finishing event".</p>
              <p><b>remaining_users</b>: Users not in any segment above.</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export const SegmentEventsStudio = () => (
  <SegmentEventsPage dataKey="segmentEventsStudio" headerPrefix="Studio (since Oct 2024)" description="First events were introduced in October 2024." />
);

export const SegmentEventsSongwriter = () => (
  <SegmentEventsPage dataKey="segmentEventsSongwriter" headerPrefix="Songwriter (since Aug 2022)" description="Most Songwriter Events were added over 1 year ago." />
);

export default SegmentEventsPage;
